import Swiper from "swiper";
import { Autoplay, Navigation } from "swiper/modules";

import { hasReducedMotion } from "../utils/a11y";
import { createSwiper } from "../utils/swiper";

const INTRO_DURATION = 1300;

export const initPostsTeaser = function (el: HTMLElement) {
	const BlockPostsTeaserObject = {
		block: el.closest(".wv-posts-teaser") as HTMLElement | null,
		container: el,
		introObserver: null as IntersectionObserver | null,
		introResizeObserver: null as ResizeObserver | null,
		introTimeout: 0,
		swiper: null as Swiper | null,
		_bindClass(): void {
			this._completeIntro = this._completeIntro.bind(this);
			this._handleIntroIntersection =
				this._handleIntroIntersection.bind(this);
			this._initIntro = this._initIntro.bind(this);
			this._initSwiper = this._initSwiper.bind(this);
			this._setIntroTransform = this._setIntroTransform.bind(this);
		},
		_bindEvents(): void {},
		_init(): void {
			console.log("Init Posts Teaser: ", this.container);

			this._bindClass();
			this._bindEvents();
			this._initSwiper();
			this._initIntro();
		},
		_completeIntro(): void {
			if (!this.block) {
				return;
			}

			window.clearTimeout(this.introTimeout);
			this.introResizeObserver?.disconnect();
			this.introResizeObserver = null;
			this.block.classList.remove(
				"wv-posts-teaser--intro-ready",
				"wv-posts-teaser--intro-revealed",
			);
			this.block.classList.add("wv-posts-teaser--intro-complete");

			if (this.swiper) {
				this.swiper.allowTouchMove = true;
				this.swiper.autoplay.start();
			}
		},
		_handleIntroIntersection(entries: IntersectionObserverEntry[]): void {
			const isVisible = entries.some(
				(entry: IntersectionObserverEntry): boolean =>
					entry.isIntersecting,
			);

			if (!isVisible || !this.block) {
				return;
			}

			this.introObserver?.disconnect();
			this.introObserver = null;

			window.requestAnimationFrame((): void => {
				this.block?.classList.add("wv-posts-teaser--intro-revealed");
				this.introTimeout = window.setTimeout(
					this._completeIntro,
					INTRO_DURATION,
				);
			});
		},
		_initIntro(): void {
			const desktopMediaQuery = window.matchMedia("(min-width: 1024px)");

			if (
				!this.block ||
				!this.swiper ||
				hasReducedMotion() ||
				window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
				!desktopMediaQuery.matches
			) {
				return;
			}

			const activeSlide = this.swiper.slides[this.swiper.activeIndex] as
				| HTMLElement
				| undefined;

			if (!activeSlide?.querySelector(".thumbnail-wrapper")) {
				return;
			}

			this.swiper.autoplay.stop();
			this.swiper.allowTouchMove = false;
			this._setIntroTransform();
			this.block.classList.add("wv-posts-teaser--intro-ready");

			this.introResizeObserver = new ResizeObserver(
				this._setIntroTransform,
			);
			this.introResizeObserver.observe(this.block);

			this.introObserver = new IntersectionObserver(
				this._handleIntroIntersection,
				{ threshold: 0.7 },
			);
			this.introObserver.observe(this.block);

			desktopMediaQuery.addEventListener(
				"change",
				(event: MediaQueryListEvent): void => {
					if (event.matches) {
						return;
					}

					this.introObserver?.disconnect();
					this.introObserver = null;
					this._completeIntro();
				},
				{ once: true },
			);
		},
		_setIntroTransform(): void {
			if (!this.block || !this.swiper) {
				return;
			}

			const activeSlide = this.swiper.slides[this.swiper.activeIndex] as
				| HTMLElement
				| undefined;
			const thumbnail =
				activeSlide?.querySelector<HTMLElement>(".thumbnail-wrapper");

			if (
				!activeSlide ||
				!thumbnail ||
				!thumbnail.offsetWidth ||
				!thumbnail.offsetHeight
			) {
				return;
			}

			const blockRect = this.block.getBoundingClientRect();
			const slideRect = activeSlide.getBoundingClientRect();
			const thumbnailCenterX =
				slideRect.left +
				thumbnail.offsetLeft +
				thumbnail.offsetWidth / 2;
			const thumbnailCenterY =
				slideRect.top +
				thumbnail.offsetTop +
				thumbnail.offsetHeight / 2;
			const blockCenterX = blockRect.left + blockRect.width / 2;
			const blockCenterY = blockRect.top + blockRect.height / 2;
			const coverScale = Math.max(
				blockRect.width / thumbnail.offsetWidth,
				blockRect.height / thumbnail.offsetHeight,
			);

			this.block.style.setProperty(
				"--wv-posts-teaser-intro-scale",
				coverScale.toString(),
			);
			this.block.style.setProperty(
				"--wv-posts-teaser-intro-translate-x",
				`${blockCenterX - thumbnailCenterX}px`,
			);
			this.block.style.setProperty(
				"--wv-posts-teaser-intro-translate-y",
				`${blockCenterY - thumbnailCenterY}px`,
			);
		},
		_initSwiper(): void {
			const nextButton = this.block?.querySelector<HTMLElement>(
				".slider-control-button--next",
			);
			const prevButton = this.block?.querySelector<HTMLElement>(
				".slider-control-button--prev",
			);
			const slideCount =
				this.container.querySelectorAll("[data-slide]").length;

			this.swiper = createSwiper(
				this.container,
				"wv-posts-teaser__slide",
				{
					modules: [Autoplay, Navigation],
					autoplay: {
						disableOnInteraction: true,
					},
					centeredSlides: true,
					loop: slideCount > 2,
					breakpoints: {
						0: {
							autoHeight: true,
							direction: "horizontal",
							slidesPerView: 1,
							spaceBetween: 24,
						},
						1024: {
							autoHeight: false,
							direction: "vertical",
							slidesPerView: 1,
							spaceBetween: 104,
						},
					},
					navigation:
						nextButton && prevButton
							? {
									nextEl: nextButton,
									prevEl: prevButton,
							  }
							: false,
					wrapperClass: "wv-posts-teaser__slider-wrapper",
				},
			);
		},
	};

	BlockPostsTeaserObject._init();
	return BlockPostsTeaserObject;
};

export const initPostsTeasers = (
	selectors = ".wv-posts-teaser__slider",
): void => {
	const blocks = document.querySelectorAll(
		selectors,
	) as NodeListOf<HTMLElement>;

	blocks.forEach((el: HTMLElement) => initPostsTeaser(el));
};
